/**
 * kit-boot-check.js
 * -----------------------------------------------------------------------------
 * A watchdog for the one failure the engine cannot report on itself.
 *
 * Every other error path ends up in `store.status` and on screen. This one
 * doesn't: if the module graph never executes. A rejected import map, a
 * Content-Security-Policy that blocks the CDN, a `kit-*.js` that 404s, a browser
 * with no import-map support. Then no engine code runs, nothing rewrites the
 * status element, and the section sits on the theme's loading placeholder
 * indefinitely. A shopper sees "Loading 3D preview…" and waits. The store owner
 * sees a broken section with no error anywhere on the page.
 *
 * So this file is deliberately a **classic** script, not a module. It is the one
 * piece of the engine that still runs when the module system has rejected
 * everything else, which is exactly when it is needed. It uses no syntax newer
 * than ES5 for the same reason. A diagnostic that needs the thing it is
 * diagnosing is no diagnostic.
 *
 * It never interferes with a healthy boot: if `window.KitStudio` shows up, it
 * does nothing at all.
 */
(function () {
  'use strict';

  /** How long to wait before calling it. Generous: a cold 8 MB model on a phone
   *  is slow, but the engine sets window.KitStudio long before the model lands,
   *  so this is only ever waiting on script execution. */
  var GRACE_MS = 8000;

  var MESSAGE = '3D preview unavailable';

  function statusNodes() {
    var out = [];
    var mounts = document.querySelectorAll('[data-kit-studio]');
    for (var i = 0; i < mounts.length; i++) {
      var node = mounts[i].querySelector('[data-kit="status"]');
      if (node) out.push(node);
    }
    return out;
  }

  /** Every <script> the page has, as a plain array. */
  function allScripts() {
    var out = [];
    var nodes = document.getElementsByTagName('script');
    for (var i = 0; i < nodes.length; i++) out.push(nodes[i]);
    return out;
  }

  /**
   * Why didn't it boot? These are the checks the page can still answer for
   * itself. Nothing here guesses: each note is something actually observed.
   *
   * Deliberately NOT checked: whether a module script loads before the import
   * map. Browsers used to reject a map in that case and it was the classic
   * cause, but current Chrome accepts both that and multiple maps, so flagging
   * it sends people to rearrange their theme for no reason. It is only still a
   * problem on older browsers, which the import-map support check below catches
   * on its own.
   */
  function diagnose() {
    var notes = [];
    var scripts = allScripts();

    // Read Three.js's URL out of the page's own import map rather than
    // hard-coding it here: pinning a second copy of that URL in a second file
    // is how a diagnostic ends up confidently probing something the page never
    // asked for, which is exactly the wrong answer to give someone debugging.
    var maps = 0;
    var threeUrl = null;
    for (var i = 0; i < scripts.length; i++) {
      if ((scripts[i].type || '').toLowerCase() !== 'importmap') continue;
      maps++;
      try {
        var imports = JSON.parse(scripts[i].textContent).imports || {};
        if (!threeUrl && imports.three) threeUrl = imports.three;
      } catch (e) {
        notes.push('An import map on this page is not valid JSON, so the browser ignored it.');
      }
    }
    if (maps === 0) {
      notes.push('No import map in the document. The section did not render one, so "three" cannot resolve.');
    }

    if (!(window.HTMLScriptElement && HTMLScriptElement.supports &&
          HTMLScriptElement.supports('importmap'))) {
      notes.push('This browser does not support import maps, which the engine needs to resolve "three".');
    }

    // Did the engine file itself arrive, and as JavaScript? A theme asset that
    // 404s, or that a redirect serves as text/html, fails the module load with
    // an error most people read as a CSP problem.
    var src = null;
    for (var j = 0; j < scripts.length; j++) {
      if ((scripts[j].src || '').indexOf('kit-studio') !== -1) src = scripts[j].src;
    }
    if (!src) {
      notes.push('No <script type="module" src=".../kit-studio.js"> on the page at all.');
      return report(notes);
    }
    if (!window.fetch) return report(notes);

    probe(src)
      .then(function (engine) {
        if (engine.error) {
          notes.push('kit-studio.js could not be fetched (' + engine.error + '). Check the asset uploaded.');
          return report(notes);
        }
        if (!engine.ok) {
          notes.push('kit-studio.js returned HTTP ' + engine.status + '. That theme asset is missing.');
          return report(notes);
        }
        if (engine.type.indexOf('javascript') === -1) {
          notes.push('kit-studio.js is served as "' + engine.type + '", not JavaScript. A module with the wrong MIME type is refused.');
          return report(notes);
        }

        // The engine file is fine, so the break is further into the graph. The
        // usual suspect is the Three.js CDN. A fetch answers connect-src rather
        // than script-src, so a success here doesn't clear CSP. But a failure
        // is conclusive.
        notes.push('kit-studio.js itself loads fine (' + engine.type + '), so the failure is inside the module graph.');
        if (!threeUrl) return report(notes);
        return probe(threeUrl).then(function (three) {
          if (three.error || !three.ok) {
            notes.push('Three.js at ' + threeUrl + ' is unreachable from this page (' +
              (three.error || 'HTTP ' + three.status) + '). Allow that origin in the ' +
              'store\'s Content-Security-Policy, or vendor Three.js into theme assets and ' +
              'repoint the import map.');
          } else {
            notes.push('Three.js is reachable, so check the console for the FIRST error above ' +
              'this one: a blocked script (CSP names the directive) or a 404 on one of the ' +
              'other kit-*.js assets, all eight of which must be uploaded.');
          }
          report(notes);
        });
      })
      .catch(function (error) {
        notes.push('diagnosis failed: ' + error.message);
        report(notes);
      });
  }

  function probe(url) {
    return fetch(url, { method: 'GET' })
      .then(function (response) {
        return {
          ok: response.ok,
          status: response.status,
          type: response.headers.get('content-type') || '(none)',
        };
      })
      .catch(function (error) {
        return { error: error.message };
      });
  }

  function report(notes) {
    console.error(
      '[kit-studio] the engine did not start within ' + GRACE_MS / 1000 + 's. ' +
      'The section is showing its loading placeholder because no engine code ran.\n' +
      (notes.length ? '  • ' + notes.join('\n  • ') : '  (no specific cause detected; check the console for earlier errors)')
    );
  }

  function check() {
    if (window.KitStudio) return; // booted. Nothing to do
    var nodes = statusNodes();
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].hidden = false;
      nodes[i].textContent = MESSAGE;
    }
    diagnose();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(check, GRACE_MS);
    });
  } else {
    setTimeout(check, GRACE_MS);
  }
})();
